import type { DrillDataConfig, DrillLegendViewModel, CategoricalLegend, NumericalLegend, DrillLegend } from '~/entities/drillHole'
import type { DrillLayer, DrillCSVFile, DrillLegendItem } from '~/entities/drillLabel'
import type { DrillLayerService, ConfigProvider } from './drill-data-set-manager'

export class DrillLegendManager implements DrillLayerService, ConfigProvider {
  registeredLegends: Map<string | number, DrillLegendViewModel>

  constructor(private onLabelsChanged: () => void) {
    this.registeredLegends = new Map()
  }

  getLayerListItems(drillDataSetId: number): DrillLayer[] {
    return this.getLegendList()
      .filter(x => x.dataSetId === drillDataSetId)
      .map(({ id, name, type }) => {
        return {
          id,
          type: 'legend',
          assayType: type,
          title: name,
          visible: this.isLegendVisible(id),
          files: [] as DrillCSVFile[],
        } as DrillLegendItem
      })
      .flat(Infinity) as DrillLayer[]
  }

  isLegendVisible(legendId: string | number): boolean {
    const legend = this.registeredLegends.get(legendId)
    return legend?.visible ?? false
  }

  setVisible(legendId: string | number, visible: boolean) {
    const legend = this.registeredLegends.get(legendId)

    if (!legend) return

    this.registeredLegends.set(legendId, { ...legend, visible })
  }

  updateLegendColor({ id: legendId, idx: gradeIndex, newColor }: { id: string; idx: number; newColor: string }) {
    const legend = this.getLegendById(legendId)

    if (!legend) return

    let collection: any[] = []

    if (legend.type === 'numerical_assay') {
      collection = (legend as NumericalLegend).grades
    } else if (legend.type === 'categorical_assay') {
      collection = (legend as CategoricalLegend).categories
    }

    collection[gradeIndex].color = newColor
  }

  registerLegends(configs: DrillDataConfig[]): void {
    const newLegends = this.generateLegends(configs)
    this.registeredLegends = new Map([...this.registeredLegends, ...newLegends])
    this.setSelectedLegendVisible(this.registeredLegends)
    this.onLabelsChanged()
  }

  setActiveLegend(legendId: string | number) {
    const legend = this.registeredLegends.get(legendId)

    if (!legend) return

    this._deselectLegendsForDataSet(legend)
    this.registeredLegends.set(legendId, { ...legend, isSelected: true, visible: true })
    this.onLabelsChanged()
  }

  getLegendList(): DrillLegendViewModel[] {
    return Array.from(this.registeredLegends.values())
  }

  getLegendById(legendId: string | number): DrillLegendViewModel | undefined {
    return this.registeredLegends.get(legendId)
  }

  removeLegendForDataSet(dataSetId: number) {
    this.registeredLegends.forEach((legend, id) => {
      if (legend.dataSetId === dataSetId) {
        this.registeredLegends.delete(id)
      }
    })
  }

  async setLegendsForScene(configs: DrillDataConfig[]) {
    if (!configs.length) return

    this.registeredLegends = this.generateLegends(configs)
    this.onLabelsChanged()
  }

  generateLegends(configs: DrillDataConfig[]): Map<string, DrillLegendViewModel> {
    const newLegends = new Map<string, DrillLegendViewModel>()

    configs.forEach(config => {
      const { data_set_id: dataSetId, numerical_assay, categorical_assay } = config

      const numericalAssays = (numerical_assay ?? []).map(assay => ({ ...assay, type: 'numerical_assay' })) as DrillLegendViewModel[]
      const categoricalAssays = (categorical_assay ?? []).map(assay => ({ ...assay, type: 'categorical_assay' })) as DrillLegendViewModel[]
      const legendTuples = this._transformAssaysToLegends([...numericalAssays, ...categoricalAssays], dataSetId)

      legendTuples.forEach(([id, legend]) => {
        newLegends.set(id, {
          ...legend,
        })
      })
    })

    return newLegends
  }

  setSelectedLegendVisible(map: Map<string | number, DrillLegendViewModel>) {
    map.forEach(value => {
      if (value.isSelected) {
        value.visible = true
      } else {
        value.visible = false
      }
    })
  }

  getConfigs(config: DrillDataConfig[]) {
    const assays = config.flatMap(x => x)
    const dataSetIds = new Set(Array.from(this.registeredLegends.values()).map(x => x.dataSetId))

    return assays
      .filter(item => dataSetIds.has(item.data_set_id))
      .map(item => {
        const dataSetId = item.data_set_id
        const res: typeof item = { ...item }

        this.registeredLegends.forEach((registeredLegend, id) => {
          if (registeredLegend.dataSetId !== dataSetId) return

          const legendId = (id as string).split('-')[1]
          const assay = res[registeredLegend.type]

          if (!Array.isArray(assay)) return
          ;(res[registeredLegend.type] as DrillLegend[]) = assay.map(legend => {
            return legend.id === legendId ? { ...legend, ...registeredLegend, id: legendId } : legend
          })
        })

        return res
      })
  }

  private _transformAssaysToLegends(assays: DrillLegendViewModel[], dataSetId: number): [string, DrillLegendViewModel][] {
    let selectedIndex = assays.findIndex((assay: DrillLegendViewModel | DrillLegend) => (assay as DrillLegendViewModel).isSelected)

    if (selectedIndex === -1) {
      selectedIndex = 0
    }

    return assays.map((assay: DrillLegendViewModel | DrillLegend, index: number) => {
      const id = `${dataSetId}-${assay.id}`

      return [
        id,
        {
          ...assay,
          id,
          type: (assay as DrillLegendViewModel).type,
          dataSetId,
          visible: (assay as DrillLegendViewModel).visible ?? false,
          isSelected: index === selectedIndex,
        },
      ]
    })
  }

  private _deselectLegendsForDataSet({ dataSetId }: DrillLegendViewModel): void {
    this.registeredLegends.forEach((legend, id) => {
      if (legend.dataSetId !== dataSetId) return
      this.registeredLegends.set(id, { ...legend, isSelected: false, visible: false })
    })
  }
}
